/**
 * Le letture dal database per i widget della dashboard.
 *
 * Ogni fonte si legge una volta per visita e la condividono tutti i widget
 * che la usano (vedi cacheFonti.ts). Le regole di accesso del database fanno
 * il resto: chi non puo' vedere le singole rate, per esempio, riceve le
 * fatture senza rate, e i conti ripiegano sulla fattura intera.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { leadService } from '../leadService';
import { emailService } from '../email/emailService';
import { fetchSubscriptionDraftInvoices, type SubscriptionDraftInvoice } from '../subscriptionInvoiceUtils';
import type { DueLeadReminder, Lead } from '../../types/leads.types';
import { TIPI_FISCALI, type CostoAnalisi, type FatturaAnalisi } from './kpiFatturato';
import type { Caricatori } from './cacheFonti';

/** Le tabelle del Mailing non sono ancora nei tipi generati del database. */
const dbSenzaTipi = supabase as unknown as SupabaseClient;

export interface AbbonamentoDashboard {
  id: string;
  monthly_amount: number | null;
  status: string;
  next_renewal_date: string | null;
  customers: { company_name: string | null } | null;
}

export interface OffertaDashboard {
  id: string;
  name: string;
  status: string;
  valid_until: string | null;
  created_by: string;
  created_at: string | null;
  customers: { company_name: string | null } | null;
}

export interface CampagnaDashboard {
  id: string;
  name: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  sent_count: number;
  delivered_count: number;
  open_count: number;
  click_count: number;
  unsubscribe_count: number;
}

export interface ContattiDashboard {
  attivi: number;
  inAttesa: number;
  /** Disiscritti, rimbalzati, reclami, soppressi. */
  fuori: number;
  nuoviMese: number;
}

export interface DatiFonti {
  fatture: FatturaAnalisi[];
  costi: CostoAnalisi[];
  abbonamenti: AbbonamentoDashboard[];
  bozzeAbbonamenti: SubscriptionDraftInvoice[];
  lead: Lead[];
  promemoriaLead: DueLeadReminder[];
  offerte: OffertaDashboard[];
  ultimaCampagna: CampagnaDashboard | null;
  contatti: ContattiDashboard;
}

export type ChiaveDati = keyof DatiFonti;

const SELECT_FATTURE =
  'id, invoice_number, invoice_date, document_type, status, payment_status, payment_due_date, total_amount, taxable_amount, customers(company_name), invoice_installments(amount, due_date, paid_at, payment_status, installment_number)';

/** Il primo del mese in corso, per contare i "nuovi del mese". */
function inizioMese(adesso = new Date()): string {
  return new Date(adesso.getFullYear(), adesso.getMonth(), 1).toISOString();
}

export const CARICATORI: Caricatori<DatiFonti> = {
  fatture: async () => {
    const { data, error } = await supabase
      .from('invoices')
      .select(SELECT_FATTURE)
      .eq('status', 'issued')
      .in('document_type', [...TIPI_FISCALI]);
    if (error) throw error;
    return (data ?? []) as unknown as FatturaAnalisi[];
  },

  costi: async () => {
    const { data, error } = await supabase
      .from('passive_invoices')
      .select('total_amount, taxable_amount, document_type, document_date')
      // Le fatture ancora "da registrare" su Fatture in Cloud non sono costi.
      .is('fic_pending_id', null);
    if (error) throw error;
    return (data ?? []) as CostoAnalisi[];
  },

  abbonamenti: async () => {
    const { data, error } = await supabase
      .from('customer_subscriptions')
      .select('id, monthly_amount, status, next_renewal_date, customers(company_name)')
      .order('next_renewal_date', { ascending: true });
    if (error) throw error;
    return (data ?? []) as unknown as AbbonamentoDashboard[];
  },

  bozzeAbbonamenti: fetchSubscriptionDraftInvoices,

  lead: () => leadService.getLeads(),

  // Tutti quelli scaduti e non fatti, anche vecchi: e' una lista di cose da fare.
  promemoriaLead: () => leadService.getDueReminders(0, Infinity),

  offerte: async () => {
    const { data, error } = await supabase
      .from('price_lists')
      .select('id, name, status, valid_until, created_by, created_at, customers(company_name)')
      .in('status', ['sent', 'accepted', 'partially_invoiced'])
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as unknown as OffertaDashboard[];
  },

  ultimaCampagna: async () => {
    const { data, error } = await dbSenzaTipi
      .from('email_campaigns')
      .select('id, name, status, started_at, finished_at, sent_count, delivered_count, open_count, click_count, unsubscribe_count')
      .in('status', ['inviata', 'in_invio'])
      .order('started_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return (data ?? null) as CampagnaDashboard | null;
  },

  contatti: async () => {
    const [conteggi, nuovi] = await Promise.all([
      emailService.getStats(),
      dbSenzaTipi
        .from('email_contacts')
        .select('id', { count: 'exact', head: true })
        .gte('created_at', inizioMese()),
    ]);
    if (nuovi.error) throw nuovi.error;
    return { attivi: conteggi.attivi, inAttesa: conteggi.inAttesa, fuori: conteggi.fuori, nuoviMese: nuovi.count ?? 0 };
  },
};
